<template>
  <div class="alarm-notifications">
    <!-- 报警弹窗列表 -->
    <transition-group name="alarm-slide" tag="div" class="notification-container">
      <div
        v-for="alarm in visibleAlarms"
        :key="alarm.id"
        class="alarm-notification"
        :class="getAlarmTypeClass(alarm.alarmType)"
        :style="{ '--alarm-color': getAlarmColor(alarm.alarmType, alarm.title) }"
        @click="handleAlarmClick(alarm)">
        
        <!-- 报警图标 -->
        <div class="alarm-icon" :style="{ background: `linear-gradient(135deg, ${getAlarmColor(alarm.alarmType, alarm.title)}, ${getAlarmColor(alarm.alarmType, alarm.title)}dd)` }">
          <span class="alarm-emoji">{{ getAlarmIcon(alarm.alarmType, alarm.title) }}</span>
        </div>
        
        <!-- 报警内容 -->
        <div class="alarm-content">
          <h4 class="alarm-title">{{ alarm.title }}</h4>
          <p class="alarm-device">设备：{{ alarm.deviceName || '未知设备' }}</p>
          <p class="alarm-location">位置：{{ alarm.location || '位置未知' }}</p>
          <p class="alarm-time">{{ formatTime(alarm.createdAt) }}</p>
        </div>
        
        <!-- 关闭按钮 -->
        <div class="alarm-close" @click.stop="dismissAlarm(alarm.id)">
          <el-icon :size="16">
            <Close />
          </el-icon>
        </div>
        
        <!-- 紧急报警闪烁效果 -->
        <div v-if="alarm.level === 'CRITICAL'" class="alarm-pulse"></div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, defineExpose } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import voiceCallService from '@/services/voiceCallService'

export default {
  name: 'AlarmNotification',
  components: {
    Close
  },
  setup() {
    console.log('AlarmNotification组件的setup函数开始执行')
    const router = useRouter()
    const visibleAlarms = ref([])
    const maxVisibleAlarms = 3 // 最多同时显示3个报警
    const autoHideTimeout = 10000 // 10秒后自动隐藏（除非是紧急报警）
    
    // 报警类型图标映射和颜色配置（与场景卡片保持一致）
    const alarmIconMap = {
      // 基础告警类型
      'SMOKE_ALARM': '🚬',
      'GAS_LEAK': '⛽', 
      'FIRE': '🔥',
      'INTRUSION': '🚨',
      'DOOR_OPEN': '🚪',
      'FALL_DETECTION': '🆘',
      'LOW_BATTERY': '🔋',
      'DEVICE_OFFLINE': '📡',
      'WATER_LEAK': '💧',
      'TEMPERATURE_HIGH': '🌡️',
      'TEMPERATURE_LOW': '❄️',
      'HUMIDITY_HIGH': '💨',
      
      // 网络故障类型
      'NETWORK_DISCONNECTED': '📡',
      'SIGNAL_WEAK': '📡',
      'NETWORK_TIMEOUT': '📡',
      'DNS_FAILURE': '📡',
      
      // 硬件故障类型
      'SENSOR_FAILURE': '⚙️',
      'HARDWARE_MALFUNCTION': '⚙️',
      'POWER_SUPPLY_FAULT': '⚡',
      'MEMORY_ERROR': '⚙️',
      'STORAGE_FULL': '⚙️',
      
      // 环境极端条件
      'EXTREME_TEMPERATURE': '🌡️',
      'ELECTROMAGNETIC_INTERFERENCE': '🌡️',
      'PHYSICAL_DAMAGE': '🌡️',
      'ENVIRONMENTAL_HAZARD': '🌡️',
      
      // 系统级故障
      'MESSAGE_QUEUE_ERROR': '💻',
      'DATABASE_CONNECTION_LOST': '💻',
      'SERVICE_UNAVAILABLE': '💻',
      'SYSTEM_OVERLOAD': '💻',
      
      // 安全攻击模拟
      'SECURITY_BREACH': '🛡️',
      'DATA_TAMPERING': '🛡️',
      'UNAUTHORIZED_ACCESS': '🛡️',
      'MALICIOUS_DEVICE': '🛡️',
      'DDOS_ATTACK': '🛡️',
      
      // 业务逻辑故障
      'DATA_CORRUPTION': '🔧',
      'CONFIGURATION_ERROR': '🔧',
      'SYNCHRONIZATION_FAILURE': '🔧',
      'BACKUP_FAILURE': '🔧',
      
      'DEFAULT': '🔔'
    }
    
    // 报警类型颜色配置（与场景卡片颜色保持一致）
    const alarmColorMap = {
      // 基础告警类型
      'FIRE': '#ff4d4f',
      'SMOKE_ALARM': '#ff4d4f', 
      'GAS_LEAK': '#fa8c16',
      'FALL_DETECTION': '#f5222d',
      'INTRUSION': '#722ed1',
      'DOOR_OPEN': '#722ed1',
      'WATER_LEAK': '#1890ff',
      'LOW_BATTERY': '#faad14',
      'DEVICE_OFFLINE': '#8c8c8c',
      'TEMPERATURE_HIGH': '#ff7875',
      'TEMPERATURE_LOW': '#40a9ff',
      'HUMIDITY_HIGH': '#52c41a',
      
      // 网络故障类型
      'NETWORK_DISCONNECTED': '#ff7875',
      'SIGNAL_WEAK': '#ff7875',
      'NETWORK_TIMEOUT': '#ff7875',
      'DNS_FAILURE': '#ff7875',
      
      // 硬件故障类型
      'SENSOR_FAILURE': '#d48806',
      'HARDWARE_MALFUNCTION': '#d48806',
      'POWER_SUPPLY_FAULT': '#fa541c',
      'MEMORY_ERROR': '#d48806',
      'STORAGE_FULL': '#d48806',
      
      // 环境极端条件
      'EXTREME_TEMPERATURE': '#ad2102',
      'ELECTROMAGNETIC_INTERFERENCE': '#ad2102',
      'PHYSICAL_DAMAGE': '#ad2102',
      'ENVIRONMENTAL_HAZARD': '#ad2102',
      
      // 系统级故障
      'MESSAGE_QUEUE_ERROR': '#531dab',
      'DATABASE_CONNECTION_LOST': '#531dab',
      'SERVICE_UNAVAILABLE': '#531dab',
      'SYSTEM_OVERLOAD': '#531dab',
      
      // 安全攻击模拟
      'SECURITY_BREACH': '#cf1322',
      'DATA_TAMPERING': '#cf1322',
      'UNAUTHORIZED_ACCESS': '#cf1322',
      'MALICIOUS_DEVICE': '#cf1322',
      'DDOS_ATTACK': '#cf1322',
      
      // 业务逻辑故障
      'DATA_CORRUPTION': '#389e0d',
      'CONFIGURATION_ERROR': '#389e0d',
      'SYNCHRONIZATION_FAILURE': '#389e0d',
      'BACKUP_FAILURE': '#389e0d',
      
      'DEFAULT': '#409eff'
    }
    
    // 获取报警图标
    const getAlarmIcon = (alarmType, alarmTitle) => {
      // 特殊场景的图标处理
      if (alarmTitle) {
        if (alarmTitle.includes('多重故障')) {
          return '⚠️'
        }
      }
      
      return alarmIconMap[alarmType] || alarmIconMap.DEFAULT
    }
    
    // 获取报警颜色
    const getAlarmColor = (alarmType, alarmTitle) => {
      // 特殊场景的颜色处理
      if (alarmTitle) {
        if (alarmTitle.includes('多重故障')) {
          return '#d46b08'
        }
      }
      
      return alarmColorMap[alarmType] || alarmColorMap.DEFAULT
    }
    
    // 获取报警样式类
    const getAlarmTypeClass = (alarmType) => {
      const criticalTypes = [
        'FIRE', 'SMOKE_ALARM', 'GAS_LEAK', 'FALL_DETECTION',
        'HARDWARE_MALFUNCTION', 'SECURITY_BREACH', 'DATA_TAMPERING',
        'MALICIOUS_DEVICE', 'DDOS_ATTACK'
      ]
      const warningTypes = [
        'INTRUSION', 'DOOR_OPEN', 'WATER_LEAK', 'NETWORK_DISCONNECTED',
        'EXTREME_TEMPERATURE', 'SYSTEM_OVERLOAD', 'DATABASE_CONNECTION_LOST',
        'SERVICE_UNAVAILABLE', 'UNAUTHORIZED_ACCESS', 'POWER_SUPPLY_FAULT'
      ]
      
      if (criticalTypes.includes(alarmType)) {
        return 'alarm-critical'
      } else if (warningTypes.includes(alarmType)) {
        return 'alarm-warning'
      } else {
        return 'alarm-info'
      }
    }
    
    // 格式化时间
    const formatTime = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      })
    }
    
    // 添加新报警通知
    const addAlarmNotification = (alarm) => {
      console.log('addAlarmNotification 被调用:', alarm)
      console.log('当前可见告警数量:', visibleAlarms.value.length)
      
      // 确保告警有ID
      const alarmId = alarm.id || alarm.alarmId || 'alarm-' + Date.now()
      
      // 检查是否已经存在相同的报警
      if (visibleAlarms.value.some(a => a.id === alarmId)) {
        console.log('告警已存在，跳过:', alarmId)
        return
      }
      
      // 如果超过最大显示数量，移除最旧的
      if (visibleAlarms.value.length >= maxVisibleAlarms) {
        visibleAlarms.value.shift()
      }
      
      // 添加新报警
      const newAlarm = {
        ...alarm,
        id: alarmId, // 确保使用统一的id字段
        autoHideTimer: null
      }
      
      console.log('添加新告警到可见列表:', newAlarm)
      visibleAlarms.value.push(newAlarm)
      console.log('添加后可见告警数量:', visibleAlarms.value.length)
      
      // 播放报警声音（如果需要）
      playAlarmSound(alarm.alarmType)
      
      // 触发语音电话（如果启用）
      handleVoiceCallTrigger(newAlarm)
      
      // 设置10秒后自动隐藏（所有告警都自动隐藏）
      const timer = setTimeout(() => {
        console.log('10秒后自动隐藏告警:', alarmId)
        dismissAlarm(alarmId)
      }, autoHideTimeout)
      
      // 更新timer引用
      const alarmIndex = visibleAlarms.value.findIndex(a => a.id === alarmId)
      if (alarmIndex !== -1) {
        visibleAlarms.value[alarmIndex].autoHideTimer = timer
      }
    }
    
    // 播放报警声音
    const playAlarmSound = (alarmType) => {
      try {
        // 优先尝试播放音频文件
        const audio = new Audio('/sounds/alarm.mp3')
        audio.volume = 0.3
        audio.play().catch(() => {
          // 如果音频文件不存在，使用Web Audio API生成提示音
          generateAlarmBeep(alarmType)
        })
      } catch (error) {
        // 如果出错，使用备用方案
        generateAlarmBeep(alarmType)
      }
    }
    
    // 使用Web Audio API生成报警提示音
    const generateAlarmBeep = (alarmType) => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        
        // 根据告警类型设置不同频率
        let frequency = 800 // 默认频率
        let duration = 200  // 默认持续时间
        
        switch(alarmType) {
          case 'FIRE':
          case 'SMOKE_ALARM': 
            frequency = 1000
            duration = 300
            break
          case 'GAS_LEAK':
          case 'SECURITY_BREACH':
            frequency = 900
            duration = 250
            break
          case 'INTRUSION':
          case 'UNAUTHORIZED_ACCESS':
            frequency = 850
            duration = 200
            break
          default:
            frequency = 800
            duration = 150
        }
        
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.type = 'sine'
        
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000)
        
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + duration / 1000)
        
      } catch (error) {
        console.log('无法生成报警声音:', error.message)
      }
    }
    
    // 处理语音电话触发
    const handleVoiceCallTrigger = async (alarmData) => {
      try {
        console.log('处理语音电话触发:', alarmData.title)
        
        // 转换告警数据格式以适配语音电话服务
        const voiceCallAlarmData = {
          title: alarmData.title,
          content: alarmData.content || alarmData.description || '安全告警',
          alarmLevelId: alarmData.alarmLevelId || alarmData.level,
          locationName: alarmData.location,
          locationInfo: alarmData.location,
          device: {
            deviceName: alarmData.deviceName || '未知设备',
            deviceTypeName: alarmData.deviceType || '安全设备',
            locationName: alarmData.location
          },
          createdAt: alarmData.createdAt || new Date().toISOString()
        }
        
        // 调用语音电话服务
        await voiceCallService.handleAlarmTrigger(voiceCallAlarmData)
        
      } catch (error) {
        console.error('语音电话触发失败:', error)
        // 不显示错误消息给用户，避免干扰告警处理流程
      }
    }
    
    // 关闭报警通知
    const dismissAlarm = (alarmId) => {
      console.log('尝试关闭告警:', alarmId)
      console.log('当前可见告警:', visibleAlarms.value.map(a => ({ id: a.id, title: a.title })))
      
      const alarmIndex = visibleAlarms.value.findIndex(a => a.id === alarmId)
      console.log('找到告警索引:', alarmIndex)
      
      if (alarmIndex !== -1) {
        const alarm = visibleAlarms.value[alarmIndex]
        if (alarm.autoHideTimer) {
          clearTimeout(alarm.autoHideTimer)
          console.log('已清除自动隐藏定时器')
        }
        visibleAlarms.value.splice(alarmIndex, 1)
        console.log('告警已从可见列表中移除')
      } else {
        console.log('未找到要关闭的告警:', alarmId)
      }
    }
    
    // 点击报警通知
    const handleAlarmClick = (alarm) => {
      // 跳转到告警中心并高亮显示该报警
      router.push({
        path: '/alarms',
        query: { highlight: alarm.id }
      })
      
      // 关闭通知
      dismissAlarm(alarm.id)
      
      ElMessage.success('已跳转到告警中心')
    }
    
    // 清除所有通知
    const clearAllNotifications = () => {
      visibleAlarms.value.forEach(alarm => {
        if (alarm.autoHideTimer) {
          clearTimeout(alarm.autoHideTimer)
        }
      })
      visibleAlarms.value = []
    }
    
    // 使用 onMounted 确保组件完全挂载后设置方法
    onMounted(() => {
      // 全局方法：添加报警通知（供外部调用）
      window.addAlarmNotification = addAlarmNotification
      console.log('AlarmNotification组件已挂载，window.addAlarmNotification已设置')
      console.log('addAlarmNotification函数:', addAlarmNotification)
      console.log('window.addAlarmNotification验证:', typeof window.addAlarmNotification)
    })
    
    onBeforeUnmount(() => {
      clearAllNotifications()
      // 清理全局方法
      if (window.addAlarmNotification) {
        delete window.addAlarmNotification
      }
    })
    
    // 暴露方法给父组件通过ref调用
    defineExpose({
      addAlarmNotification,
      clearAllNotifications,
      dismissAlarm
    })
    
    console.log('defineExpose 已执行，暴露的方法:', {
      addAlarmNotification: typeof addAlarmNotification,
      clearAllNotifications: typeof clearAllNotifications,
      dismissAlarm: typeof dismissAlarm
    })
    
    console.log('AlarmNotification组件setup函数执行完成')
    
    return {
      visibleAlarms,
      getAlarmIcon,
      getAlarmColor,
      getAlarmTypeClass,
      formatTime,
      handleAlarmClick,
      dismissAlarm
    }
  }
}
</script>

<style scoped>
.alarm-notifications {
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 2000;
  pointer-events: none;
}

.notification-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.alarm-notification {
  width: 350px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-left: 4px solid var(--alarm-color, #409eff);
  padding: 16px;
  cursor: pointer;
  position: relative;
  pointer-events: auto;
  transition: all 0.3s ease;
}

.alarm-notification:hover {
  transform: translateX(-5px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.alarm-notification {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.alarm-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.alarm-emoji {
  font-size: 20px;
  line-height: 1;
}

.alarm-content {
  flex: 1;
  min-width: 0;
}

.alarm-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.alarm-device,
.alarm-location,
.alarm-time {
  margin: 2px 0;
  font-size: 13px;
  color: #666;
  line-height: 1.3;
}

.alarm-time {
  color: #999;
  font-weight: 500;
}

.alarm-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.alarm-close:hover {
  background: rgba(0, 0, 0, 0.2);
  color: #333;
}

/* 不同报警类型的样式 */
.alarm-critical {
  background: linear-gradient(135deg, #fff 0%, rgba(255, 77, 79, 0.08) 100%);
}

.alarm-critical .alarm-icon {
  animation: pulse-critical 1.5s ease-in-out infinite;
}

.alarm-warning {
  background: linear-gradient(135deg, #fff 0%, rgba(250, 140, 22, 0.08) 100%);
}

.alarm-info {
  background: linear-gradient(135deg, #fff 0%, rgba(64, 158, 255, 0.08) 100%);
}

/* 紧急报警脉冲效果 */
.alarm-pulse {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border: 2px solid var(--alarm-color, #f56c6c);
  border-radius: 8px;
  animation: pulse-border 1.5s ease-in-out infinite;
}

/* 动画效果 */
.alarm-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.alarm-slide-leave-active {
  transition: all 0.3s ease-in;
}

.alarm-slide-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.alarm-slide-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.alarm-slide-move {
  transition: transform 0.3s ease;
}

@keyframes pulse-critical {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes pulse-border {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .alarm-notifications {
    top: 60px;
    right: 10px;
    left: 10px;
  }
  
  .alarm-notification {
    width: auto;
  }
}
</style>